import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import { type Awareness, encodeAwarenessUpdate } from 'y-protocols/awareness';

// This bridge only retains opaque geometry. Geometry interpretation remains
// with the canvas owner; clocks and client lifecycle remain with Y.Awareness.
type Position = { x: number; y: number };
type MovePreview = {
  dragId: string;
  basisVersion: number;
  operation: 'move';
  status: 'dragging';
  translation: Position;
  basisTranslation?: Position;
  elements: unknown[];
  elementIds: unknown[];
  compact?: true;
};

function isPosition(value: unknown): value is Position {
  return (
    !!value &&
    typeof value === 'object' &&
    'x' in value &&
    'y' in value &&
    typeof value.x === 'number' &&
    Number.isFinite(value.x) &&
    typeof value.y === 'number' &&
    Number.isFinite(value.y)
  );
}

function isMovePreview(value: unknown): value is MovePreview {
  return (
    !!value &&
    typeof value === 'object' &&
    'dragId' in value &&
    typeof value.dragId === 'string' &&
    'basisVersion' in value &&
    typeof value.basisVersion === 'number' &&
    Number.isSafeInteger(value.basisVersion) &&
    value.basisVersion >= 0 &&
    'operation' in value &&
    value.operation === 'move' &&
    'status' in value &&
    value.status === 'dragging' &&
    'translation' in value &&
    isPosition(value.translation)
  );
}

const receipt = (preview: MovePreview) =>
  JSON.stringify([preview.dragId, preview.basisVersion]);

/** Collect always sends the canonical complete state. Compact movement is
 * opt-in: every known peer must have received this exact geometry basis. */
export function encodeCanvasDragAwareness(
  awareness: Awareness,
  clients: number[],
  collect = false,
  canonicalUpdate?: Uint8Array
): Uint8Array {
  const fullUpdate =
    canonicalUpdate ?? encodeAwarenessUpdate(awareness, clients);
  const states = awareness.getStates();
  const local = states.get(awareness.clientID);
  const preview = local?.canvasDragPreview;
  if (
    collect ||
    !clients.includes(awareness.clientID) ||
    !isMovePreview(preview) ||
    !Array.isArray(preview.elements) ||
    !Array.isArray(preview.elementIds)
  )
    return fullUpdate;

  const basisReceipt = receipt(preview);
  let acknowledgedPeer = false;
  for (const [clientId, state] of states) {
    if (
      clientId !== awareness.clientID &&
      state.canvasDragPreviewBasisAck?.[awareness.clientID] !== basisReceipt
    )
      return fullUpdate;
    if (clientId !== awareness.clientID) acknowledgedPeer = true;
  }
  if (!acknowledgedPeer) return fullUpdate;
  const { elements: _elements, elementIds: _ids, ...movement } = preview;
  const wireStates = new Map(states);
  wireStates.set(awareness.clientID, {
    ...local,
    canvasDragPreview: { ...movement, compact: true },
  });
  return encodeAwarenessUpdate(awareness, clients, wireStates);
}

/** Hydrate compact frames before applying Y.Awareness clocks. A missing basis
 * must not advance the clock: a full collect of that same clock can still win. */
export function decodeCanvasDragAwareness(
  awareness: Awareness,
  update: Uint8Array
): Uint8Array {
  const decoder = decoding.createDecoder(update);
  const count = decoding.readVarUint(decoder);
  let changed = false;
  const entries: Array<{ clientId: number; clock: number; state: unknown }> =
    [];
  for (let i = 0; i < count; i++) {
    const clientId = decoding.readVarUint(decoder);
    const clock = decoding.readVarUint(decoder);
    let state = JSON.parse(decoding.readVarString(decoder));
    const preview = state?.canvasDragPreview;
    if (preview?.compact === true) {
      changed = true;
      const basis = awareness.getStates().get(clientId)?.canvasDragPreview;
      if (
        !isMovePreview(preview) ||
        !isMovePreview(basis) ||
        receipt(preview) !== receipt(basis) ||
        !Array.isArray(basis.elements) ||
        !Array.isArray(basis.elementIds)
      )
        continue;
      const { compact: _compact, ...movement } = preview;
      state = {
        ...state,
        canvasDragPreview: {
          ...movement,
          elements: basis.elements,
          elementIds: basis.elementIds,
          basisTranslation: basis.basisTranslation ?? basis.translation,
        },
      };
    }
    entries.push({ clientId, clock, state });
  }
  if (!changed) return update;
  const encoder = encoding.createEncoder();
  encoding.writeVarUint(encoder, entries.length);
  for (const { clientId, clock, state } of entries) {
    encoding.writeVarUint(encoder, clientId);
    encoding.writeVarUint(encoder, clock);
    encoding.writeVarString(encoder, JSON.stringify(state));
  }
  return encoding.toUint8Array(encoder);
}

/** Receipts are session-scoped and pruned when the drag/actor disappears. */
export function acknowledgeCanvasDragBases(awareness: Awareness): void {
  const local = awareness.getLocalState();
  if (!local) return;
  const next: Record<string, string> = {};
  for (const [clientId, state] of awareness.getStates()) {
    if (clientId === awareness.clientID) continue;
    const preview = state.canvasDragPreview;
    if (
      isMovePreview(preview) &&
      Array.isArray(preview.elements) &&
      Array.isArray(preview.elementIds)
    )
      next[clientId] = receipt(preview);
  }
  if (
    JSON.stringify(local.canvasDragPreviewBasisAck ?? {}) !==
    JSON.stringify(next)
  )
    awareness.setLocalStateField('canvasDragPreviewBasisAck', next);
}
